import { ActionMonitorRun, ActionMonitorStep, xactPassLabel, xactPassesOf } from '../models/action-monitor.model';

/**
 * THE RUN AS PLAIN TEXT (XAT-E-253-03) — what **Copy log** puts on the clipboard.
 *
 * A pure function over the run, in its own file and with its own spec, because it is the ONE part of the
 * monitor that leaves the app: it is pasted into a ticket, a chat, a commit message. What it says has to
 * be readable by somebody who never saw the panel — which is why every row carries its own number, its
 * action, what it was for and how it ended, and why the batch headers are kept: a run that stopped in
 * batch 2 reads as one.
 *
 * It is deliberately NOT JSON. The developer copying this is explaining a run to a person.
 */
export function formatRunLog(run: ActionMonitorRun | null): string {
  if (!run) return '';

  const lines: string[] = [];

  // The header: what ran, what it was working in, how it ended and how long it took.
  const heading = [run.title, run.context].filter(part => !!part && `${part}`.trim().length > 0).join(' · ');
  lines.push(heading);
  lines.push(`${run.outcome || statusSentence(run)}  ·  ${elapsedOf(run)}  ·  ${run.done} / ${run.total} succeeded`);

  let group: number | null = null;

  for (const step of run.steps) {
    // Batch headers only where the run actually has more than one: a single-batch run's header would say
    // nothing the heading above has not already said.
    if (hasGroups(run) && (step.groupIndex ?? 1) !== group) {
      group = step.groupIndex ?? 1;
      lines.push('');
      lines.push(`Batch ${group}${step.groupSummary ? ` · ${step.groupSummary}` : ''}`);
    }

    lines.push(rowOf(step));

    // Under the row, and only when they add something: the expectation says what was supposed to happen
    // and the engine's text says what did not, and a failed run is read by comparing the two.
    // A loop's row (XCT-E-14-07) is its latest pass only, so the log says which pass that was.
    const passes = xactPassesOf(step);
    if (passes.length) lines.push(`      ${passes.map(xactPassLabel).join(' · ')}`);
    if (step.expectation) lines.push(`      expected: ${step.expectation}`);
    // A background row (XAT-E-255-01) names what it dispatched and where — the thing a ticket about a
    // failed command batch actually needs.
    if (step.command) lines.push(`      command: ${step.command}`);
    if (step.error) lines.push(`      error: ${step.error}`);
    // A write step's rows (XAT-E-336-07) - what was staged, what each command wrote and did not - one line
    // each: what a ticket about a half-landed write needs.
    for (const line of step.details ?? []) lines.push(`      ${line}`);
  }

  return lines.join('\n');
}

/** `s3  CLICK  Add Scenario button  ✓  0.9s` — the number, the action, the words, the ending. */
function rowOf(step: ActionMonitorStep): string {
  const parts = [
    `s${step.ordinal}`,
    (step.actionType || '').trim().toUpperCase() || '—',
    headlineOf(step),
    markOf(step)
  ];

  const duration = formatDuration(step.durationMs);
  if (duration) parts.push(duration);

  return parts.join('  ');
}

/** What the row leads with — the same rule the panel renders by. */
function headlineOf(step: ActionMonitorStep): string {
  return step.expectation || step.description || step.actionType || step.key || '';
}

/** How the row ended, in one token. A step the run never reached says so in words, not as a symbol. */
function markOf(step: ActionMonitorStep): string {
  switch (step.status) {
    case 'success': return '✓';
    case 'failed': return '✗';
    case 'stopped': return 'stopped';
    case 'not-run': return 'not run';
    case 'skipped': return 'skipped';
    case 'running': return 'running…';
    default: return 'not run';
  }
}

function hasGroups(run: ActionMonitorRun): boolean {
  return new Set(run.steps.map(step => step.groupIndex ?? 1)).size > 1;
}

/**
 * The run's own clock: what it actually took when it has settled, and how long it has been going when it
 * has not. Read off the run rather than from `Date.now()` at copy time, so a log copied from a reopened
 * run says what that run took and not how long ago it was.
 */
function elapsedOf(run: ActionMonitorRun): string {
  const end = run.finishedAt ?? Date.now();
  return formatDuration(Math.max(0, end - run.startedAt)) || '0.0s';
}

/**
 * `40ms`, `0.9s`, `2m 05s`. SECONDS from a tenth of a second up, unlike the panel's own spelling: the log
 * is read as a column of rows being compared to each other, and mixing `900ms` into a list of `1.2s` makes
 * the eye do arithmetic. Only the genuinely instant steps keep milliseconds, where a `0.0s` would say less.
 */
function formatDuration(ms?: number): string {
  if (ms == null || ms <= 0) return '';
  if (ms < 100) return `${Math.round(ms)}ms`;

  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;

  return `${Math.floor(seconds / 60)}m ${`${Math.round(seconds % 60)}`.padStart(2, '0')}s`;
}

/** The fallback for a run with no outcome sentence of its own — one that is still going. */
function statusSentence(run: ActionMonitorRun): string {
  return run.status === 'running' ? 'Running…' : run.status;
}
